<script setup>
import { ref, computed, watch } from 'vue';
import { Head } from '@inertiajs/vue3';
import InputText from 'primevue/inputtext';
import IconField from 'primevue/iconfield';
import InputIcon from 'primevue/inputicon';
import { Link, usePage } from '@inertiajs/vue3';
import { Dialog, DialogTitle, DialogPanel, Disclosure, DisclosureButton, DisclosurePanel, Menu, MenuButton, MenuItem, MenuItems, Popover, PopoverButton, PopoverGroup, PopoverPanel, TransitionChild, TransitionRoot, } from '@headlessui/vue'

import moment from 'moment';
import Paginator from 'primevue/paginator';
import { hasPermission } from '../../../Composables/userHelper';

let eurFormatter = new Intl.NumberFormat('nl-NL', {
  style: 'currency',
  currency: 'EUR',
  minimumFractionDigits: 2,
  maximumFractionDigits: 2
});

const mobileFiltersOpen = ref(false)

const props = defineProps({ 
  orders: Array,
  paidAmount: Number,
  overdueAmount: Number,
  pendingAmount: Number,
  toInvoiceOrders: Array
})

const page = usePage();

const user = page.props.auth.user;

const sortOptions = [
    { name: 'Afgelopen 30 dagen', href: '?sort=30' },
    { name: 'Afgelopen 60 dagen', href: '?sort=60' },
    { name: 'Afgelopen 90 dagen', href: '?sort=90' },
    { name: '90 dagen of ouder', href: '?sort=91' },
    { name: 'Alle data', href: '?sort=all' }
]

const statuses = {
  paid: 'text-green-700 bg-green-50 ring-green-600/20',
  pending: 'text-gray-600 bg-gray-50 ring-gray-500/10',
  overdue: 'text-red-700 bg-red-50 ring-red-600/10',
}

function getCurrentSortOption() {
    //get url sort parameter
    let urlParams = new URLSearchParams(window.location.search);
    let sort = urlParams.get('sort');
    if (sort) {
        let sortOption = sortOptions.find(option => option.href.includes(sort));
        return sortOption.name;
    } else {
        return 'Afgelopen 30 dagen';
    }
}

const pageOrders = computed(() => page.props.orders);

watch(pageOrders, () => {
    orders.value = pageOrders.value;
    orders.value = doFilter();
    //set paginator to page 1
    if (paginator.value) {
        paginator.value.changePage(0);
    }
})

const orders = ref(props.orders);
const paginator = ref(null);
const paginatedData = ref([]);
const searchValue = ref('');

watch(searchValue, (newValue) => {
    orders.value = doFilter();
});

watch(orders, () => {
    if (paginator.value) {
        paginator.value.changePage(0);
    }
    changePagination({ rows: 10, page: 0 });
});

function changePagination(pageState) {
    //get row count
    const rows = pageState.rows;
    //get page count
    const page = pageState.page;

    //calculate start index
    const startIndex = rows * page;
    //calculate end index
    const endIndex = startIndex + rows;
    //get the data for the current page

    paginatedData.value = orders.value.slice(startIndex, endIndex);
}

changePagination({ rows: 10, page: 0 });

function doFilter() {
  const filteredOrders = props.orders.filter(order => {
    if (activeStatusFilters.value.length > 0 && !activeStatusFilters.value.includes(order.status)) return false

    return true;
  });

  if (searchValue.value) {
    return filteredOrders.filter(order => {
      //check date
      if (moment(order.created_at).format('DD-MM-YYYY').includes(searchValue.value)) return true;
    });
  }

  return filteredOrders;
}

const filters = [
    {
        id: 'status',
        name: 'Status',
        options: [
            { value: 'paid', label: 'Voldaan' },
            { value: 'pending', label: 'Open' },
            { value: 'overdue', label: 'Te laat' }
        ],
    },
]

const activeStatusFilters = ref([]);

function getModel(sectionId) {
    switch (sectionId) {
        case 'status':
            return activeStatusFilters
    }
}

watch(activeStatusFilters, () => {
  orders.value = doFilter();
})

function getStatus(status) {
    switch (status) {
        case 'paid':
            return 'Voldaan';
        case 'pending':
            return 'Open';
        case 'overdue':
            return 'Te laat';
    }
}



const pageToInvoiceOrders = computed(() => page.props.toInvoiceOrders);

const toInvoiceOrders = ref(props.toInvoiceOrders);
const toInvoiceOrdersPaginator = ref(null);
const paginatedToInvoiceOrdersData = ref([]);

watch(pageToInvoiceOrders, () => {
  toInvoiceOrders.value = pageToInvoiceOrders.value;
    //set paginator to page 1
    if (toInvoiceOrdersPaginator.value) {
        toInvoiceOrdersPaginator.value.changePage(0);
    }
})

watch(toInvoiceOrders, () => {
    if (toInvoiceOrdersPaginator.value) {
        toInvoiceOrdersPaginator.value.changePage(0);
    }
    changeToInvoiceOrdersPagination({ rows: 10, page: 0 });
});

function changeToInvoiceOrdersPagination(pageState) {
    //get row count
    const rows = pageState.rows;
    //get page count
    const page = pageState.page;

    //calculate start index
    const startIndex = rows * page;
    //calculate end index
    const endIndex = startIndex + rows;
    //get the data for the current page

    paginatedToInvoiceOrdersData.value = toInvoiceOrders.value.slice(startIndex, endIndex);
}

changeToInvoiceOrdersPagination({ rows: 10, page: 0 });

const deleteDialog = ref(false);

let deleteDialogText = "";
let deleteDialogUrl = "";

function openDeleteDialog(item) {
  deleteDialogText = item.id;
  deleteDialogUrl = "/portal/facturen/" + item.id + "/delete";
  deleteDialog.value = true;
}

</script>

<template>
  <Head title="Facturen | Mijnautosnelweg" />
  <main class="px-4 sm:px-6 py-6 space-y-8">
    <div class="">
      <nav class="sm:hidden" aria-label="Back">
        <Link href="/portal/dashboard" class="flex items-center text-sm font-medium text-gray-500 hover:text-gray-700 gap-x-1">
            <font-awesome-icon :icon="['fas', 'chevron-left']" class="h-3 w-3 text-gray-400 shrink-0" />
            Terug
        </Link>
      </nav>
      <nav class="hidden sm:flex" aria-label="Breadcrumb">
        <ol order="list" class="flex items-center space-x-4">
          <li>
            <div class="flex">
              <Link href="/portal/dashboard" class="text-sm font-medium text-gray-500 hover:text-gray-700">Dashboard
              </Link>
            </div>
          </li>
          <font-awesome-icon :icon="['fas', 'chevron-right']" class="h-3 w-3 text-gray-400 shrink-0" />
          <li>
            <div class="flex items-center">
              <Link href="#" class="text-sm font-medium text-gray-900 hover:text-gray-700">
              Facturen</Link>
            </div>
          </li>
        </ol>
      </nav>
    </div>
    <div class="flex flex-col sm:flex-row justify-between w-full gap-y-2">
      <div class="flex flex-col">
        <h1 class="text-base font-semibold leading-6 text-gray-900">Mijn facturen</h1>
      </div>
      <div class="flex flex-row gap-3 items-center w-full sm:w-auto">
        <IconField iconPosition="right" class="w-full sm:w-auto">
          <InputIcon class="pi pi-search"> </InputIcon>
          <InputText class="h-10" v-model="searchValue" placeholder="Search" />
        </IconField>
        <Link href="/portal/facturen/aanmaken" class="cursor-pointer">
          <font-awesome-icon :icon="['fal', 'plus']" class="w-5 h-5 p-2.5 bg-royalblue-500 hover:bg-royalblue-500/80 text-white shadow-sm rounded-lg shrink-0" aria-hidden="true" />
        </Link>
      </div>
    </div>
    <div class="grid grid-cols-1 sm:grid-cols-3 gap-4">
      <div class="ring-1 rounded-lg p-6 flex flex-col items-center">
        <font-awesome-icon :icon="['far', 'money-bill-trend-up']" class="h-8 w-8 text-gray-900 shrink-0" aria-hidden="true" />
        <h3 class="text-lg font-semibold leading-6 text-gray-900 pt-4">{{ eurFormatter.format(props.paidAmount) }}</h3>
        <p class="text-gray-900">Voldaan bedrag</p>
      </div>
      <div class="ring-1 rounded-lg p-6 flex flex-col items-center">
        <font-awesome-icon :icon="['far', 'envelope-open-dollar']" class="h-8 w-8 text-gray-900 shrink-0" aria-hidden="true" />
        <h3 class="text-lg font-semibold leading-6 text-gray-900 pt-4">{{ eurFormatter.format(props.pendingAmount) }}</h3>
        <p class="text-gray-900">Openstaand bedrag</p>
      </div>
      <div class="ring-1 rounded-lg p-6 flex flex-col items-center">
        <font-awesome-icon :icon="['far', 'hourglass-clock']" class="h-8 w-8 text-gray-900 shrink-0" aria-hidden="true" />
        <h3 class="text-lg font-semibold leading-6 text-gray-900 pt-4">{{ eurFormatter.format(props.overdueAmount) }}</h3>
        <p class="text-gray-900">Achterstallig bedrag</p>
      </div>
    </div>
    <div>
      <!-- Mobile filter dialog -->
      <TransitionRoot as="template" :show="mobileFiltersOpen">
          <Dialog class="relative z-40 xl:hidden" @close="mobileFiltersOpen = false">
              <TransitionChild as="template" enter="transition-opacity ease-linear duration-300"
                  enter-from="opacity-0" enter-to="opacity-100"
                  leave="transition-opacity ease-linear duration-300" leave-from="opacity-100"
                  leave-to="opacity-0">
                  <div class="fixed left-0 right-0 top-0 bottom-0 bg-black bg-opacity-5" />
              </TransitionChild>
              <div class="fixed left-0 right-0 top-0 bottom-0 z-40 flex">
                  <TransitionChild as="template" enter="transition ease-in-out duration-300 transform"
                      enter-from="translate-x-full" enter-to="translate-x-0"
                      leave="transition ease-in-out duration-300 transform" leave-from="translate-x-0"
                      leave-to="translate-x-full">
                      <DialogPanel
                          class="relative ml-auto flex h-full w-full max-w-xs bg-white flex-col overflow-y-auto  py-4 pb-6 shadow-xl">
                          <div class="flex items-center justify-between px-4">
                              <h2 class="text-lg font-medium text-gray-900">Filters</h2>
                              <button type="button"
                                  class="-mr-2 flex h-10 w-10 items-center justify-center rounded-md  p-2 text-gray-400 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-brightorange"
                                  @click="mobileFiltersOpen = false">
                                  <span class="sr-only">Close menu</span><font-awesome-icon :icon="['far', 'xmark']" class="h-6 w-6 shrink-0"/>
                              </button>
                          </div>

                          <!-- Filters -->
                          <form class="mt-4">
                              <Disclosure as="div" v-for="section in filters" :key="section.name"
                                  class="border-t border-gray-200 px-4 py-6" v-slot="{ open }">
                                  <h3 class="-mx-2 -my-3 flow-root">
                                      <DisclosureButton
                                          class="flex w-full items-center justify-between  px-2 py-3 text-sm text-gray-400">
                                          <span class="font-medium text-gray-900">{{ section.name }}</span>
                                          <span class="ml-6 flex items-center">
                                              <font-awesome-icon :icon="['far', 'chevron-down']" rotation="90" :class="{ 'h-4 w-4 text-royalblue-500 fa-rotate': open, 'hidden': !open }" aria-hidden="true" />
                                              <font-awesome-icon :icon="['far', 'chevron-right']" rotation="180" :class="{ 'hidden': open, 'h-4 w-4 text-royalblue-500': !open }" aria-hidden="true" />
                                          </span>
                                      </DisclosureButton>
                                  </h3>
                                  <DisclosurePanel class="pt-6">
                                      <div class="space-y-2">
                                          <div v-for="(option, optionIdx) in section.options" :key="option.value"
                                              class="flex items-center">
                                              <input :id="`filter-mobile-${section.id}-${optionIdx}`" v-model="getModel(section.id).value"
                                              :value="option.value" :checked="getModel(section.id).value.includes(option.value)" type="checkbox" 
                                                  class="h-4 w-4 ring-1 ring-inset ring-gray-300 rounded border-gray-300 text-brightorange focus:ring-brightorange" />
                                              <label :for="`filter-mobile-${section.id}-${optionIdx}`"
                                                  class="ml-3 text-sm text-gray-500">{{ option.label }}</label>
                                          </div>
                                      </div>
                                  </DisclosurePanel>
                              </Disclosure>
                          </form>
                      </DialogPanel>
                  </TransitionChild>
              </div>
          </Dialog>
      </TransitionRoot>

      <!-- Filters -->
      <section aria-labelledby="filter-heading">
          <h2 id="filter-heading" class="sr-only">Product filters</h2>

          <div class="flex items-center justify-between">

              <button type="button"
                  class="ring-1 ring-inset ring-gray-300 rounded-lg py-3.5 px-2.5 flex flex-row gap-2 text-sm font-medium text-gray-700 hover:text-gray-900 xl:hidden"
                  @click="mobileFiltersOpen = true">
                  <font-awesome-icon :icon="['far', 'filter']" class="h-5 w-5 shrink-0"/>
                  Filters
              </button>

              <PopoverGroup class="hidden xl:flex md:items-baseline sm:space-x-8">
                  <Popover as="div" v-for="(section, sectionIdx) in filters" :key="section.name" id="menu"
                      class="relative inline-block text-left">
                      <PopoverButton
                          class="group inline-flex items-center justify-center text-sm font-medium text-gray-700 hover:text-gray-900">
                          <span>{{ section.name }}</span>
                          <span v-if="getModel(section.id).value.length > 0"
                              class="ml-1.5 rounded bg-gray-200 px-1.5 py-0.5 text-xs font-semibold tabular-nums text-gray-700">{{getModel(section.id).value.length}}</span>
                          <font-awesome-icon :icon="['far', 'chevron-right']" rotation="90" class="h-4 w-4 text-royalblue-500" aria-hidden="true" />
                      </PopoverButton>

                      <transition enter-active-class="transition ease-out duration-100"
                          enter-from-class="transform opacity-0 scale-95"
                          enter-to-class="transform opacity-100 scale-100"
                          leave-active-class="transition ease-in duration-75"
                          leave-from-class="transform opacity-100 scale-100"
                          leave-to-class="transform opacity-0 scale-95">
                          <PopoverPanel
                              class="absolute max-h-64 overflow-scroll bg-white border z-10 mt-2 origin-top-right rounded-md  p-4 shadow-2xl ring-1 ring-black ring-opacity-5 focus:outline-none">
                              <form class="space-y-4">
                                  <div v-for="(option, optionIdx) in section.options" :key="option.value" 
                                      class="flex items-center">
                                      <input :id="`filter-${section.id}-${optionIdx}`" v-model="getModel(section.id).value"
                                          :value="option.value" :checked="getModel(section.id).value.includes(option.value)" type="checkbox" 
                                          class="h-4 w-4 ring-1 ring-inset ring-gray-300 rounded border-gray-300 text-brightorange focus:ring-brightorange" />
                                      <label :for="`filter-${section.id}-${optionIdx}`"
                                          class="ml-3 whitespace-nowrap pr-6 text-sm font-medium text-gray-900">{{
                                          option.label
                                          }}</label>
                                  </div>
                              </form>
                          </PopoverPanel>
                      </transition>
                  </Popover>
              </PopoverGroup>

              <Menu as="div" class="relative inline-block text-left">
                  <div class="flex flex-row gap-2 items-center">

                      <MenuButton
                          class="group inline-flex justify-center items-center text-sm font-medium text-gray-700 hover:text-gray-900">
                          {{getCurrentSortOption()}}
                          <font-awesome-icon :icon="['fal', 'sort']" class="h-4 w-4 text-royalblue-500" aria-hidden="true" />
                      </MenuButton>
                  </div>

                  <transition enter-active-class="transition ease-out duration-100"
                      enter-from-class="transform opacity-0 scale-95"
                      enter-to-class="transform opacity-100 scale-100"
                      leave-active-class="transition ease-in duration-75"
                      leave-from-class="transform opacity-100 scale-100"
                      leave-to-class="transform opacity-0 scale-95">
                      <MenuItems
                          class="absolute bg-white right-0 border z-10 mt-2 w-48 origin-top-left rounded-md  shadow-2xl ring-1 ring-black ring-opacity-5 focus:outline-none">
                          <div class="py-1">
                              <MenuItem v-for="option in sortOptions" :key="option"
                                  v-slot="{ sortOptionSelected }">
                              <Link :href="option.href"
                                  :class="[sortOptionSelected ? 'bg-gray-100' : '', 'block px-4 py-2 text-sm font-medium text-gray-900']">{{
                                  option.name
                                  }}</Link>
                              </MenuItem>
                          </div>
                      </MenuItems>
                  </transition>
              </Menu>
          </div>
      </section>
    </div>
    <div class="mt-10 ring-1 ring-gray-300 rounded-lg w-full" v-if="paginatedData.length > 0">
      <table class="min-w-full divide-y divide-gray-300 border-b">
        <thead class="">
          <tr>
            <th scope="col"
              class="hidden xl:table-cell px-3 py-3.5 flex-1 text-left text-sm font-semibold text-gray-900">
              Status
            </th>
            <th scope="col" class="px-3 py-3.5 flex-1 text-left text-sm font-semibold text-gray-900">
              Factuurnummer</th>
            <th scope="col" class="px-3 py-3.5 flex-1 text-left text-sm font-semibold text-gray-900">
              Kavelnummer</th>
            <th scope="col"
              class="hidden sm:table-cell px-3 py-3.5 flex-1 text-left text-sm font-semibold text-gray-900">
              Verzonden op
            </th>
            <th v-if="!user.dealer" scope="col" class="px-3 py-3.5 flex-1 text-left text-sm font-semibold text-gray-900">
              Klant</th>
            <th scope="col"
              class="hidden md:table-cell px-3 py-3.5 flex-1 text-left text-sm font-semibold text-gray-900">
              Factuur vervaldatum
            </th>
            <th scope="col"
              class="hidden lg:table-cell px-3 py-3.5 flex-1 text-left text-sm font-semibold text-gray-900">
              Bedrag
            </th>
          </tr>
        </thead>
        <tbody class="divide-y">
          <template v-for="order in paginatedData">
            <tr>
              <td class="hidden xl:table-cell whitespace-nowrap px-3 py-4 text-sm text-gray-500">
                <div class="flex flex-auto">
                  <div
                    :class="[statuses[order.status], 'rounded-full px-3.5 py-1.5 text-xs font-medium ring-1 ring-inset']">
                    {{ getStatus(order.status) }}</div>
                </div>
              </td>
              <td class="table-cell whitespace-nowrap px-3 py-4 text-sm text-gray-500">{{
                order.id }}</td>
              <td class="table-cell whitespace-nowrap px-3 py-4 text-sm text-gray-500">{{
                order.lot_id }}</td>
              <td class="hidden sm:table-cell whitespace-nowrap px-3 py-4 text-sm text-gray-500">{{
                moment(order.created_at).format('DD-MM-YYYY') }}</td>
              <td v-if="!user.dealer" class="table-cell whitespace-nowrap px-3 py-4 text-sm text-gray-500">{{
                order.user.full_name }}</td>
              <td class="hidden md:table-cell whitespace-nowrap px-3 py-4 text-sm text-gray-500">{{
                moment(order.created_at).add(2, 'W').format('DD-MM-YYYY') }}</td>
              <td class="hidden lg:table-cell whitespace-nowrap px-3 py-4 text-sm text-gray-500">
                <div class="flex items-start gap-x-3">
                  <div class="text-sm font-medium leading-6 text-gray-900">{{ eurFormatter.format(order.fee + order.extra_price - order.discount) }} fee</div>
                  <div class="mt-1 text-xs leading-5 text-gray-500">{{ eurFormatter.format(order.price) }}</div>
                </div>
              </td>
              <td
                class="hidden lg:flex whitespace-nowrap py-4 pr-4 pl-3 text-right text-sm font-medium gap-1.5">
                <Link :href="'/portal/facturen/'+order.id" class="text-royalblue-500 hover:text-royalblue-500/80 ml-auto">
                Bekijk</Link>  <span  v-if="hasPermission('order-destroy')">| <button class="text-royalblue-500 hover:text-royalblue-500/80" @click="openDeleteDialog(order)">
                  Verwijderen</button></span>
              </td>
              <td class="flex lg:hidden whitespace-nowrap py-4 text-sm gap-1.5 pr-3">
                <Link :href="'/portal/facturen/' + order.id"
                class="text-royalblue-500 hover:text-royalblue-500/80">
                  <font-awesome-icon :icon="['far', 'eye']" class="h-5 w-5 shrink-0"/>
                <span class="sr-only">,
                {{
                order.id }}</span></Link>
                <span  v-if="hasPermission('order-destroy')">
                  <font-awesome-icon @click="openDeleteDialog(order)" :icon="['fal', 'trash-can']" class="w-5 h-5 text-royalblue-500 hover:text-royalblue-500/80 hover:cursor-pointer shrink-0" />
                </span>
            </td>
            </tr>
          </template>
        </tbody>
      </table>
    </div>
    <div v-else>
      <div class="flex flex-col items-center justify-center h-96">
        <font-awesome-icon :icon="['fal', 'file-invoice']" class="h-12 w-12 text-gray-400 shrink-0" />
        <p class="mt-4 text-sm text-gray-500">Geen facturen gevonden</p>
      </div>
    </div>
          
    <Paginator ref="paginator" v-if="orders.length > 10" @page="changePagination" :rows="10"
      :totalRecords="orders.length" :rowsPerPageOptions="[10, 20, 30]"></Paginator>

    <div class="flex flex-col" v-if="!user.dealer">
      <h1 class="text-base font-semibold leading-6 text-gray-900">Nog te factureren</h1>
    </div>

    <div class="mt-10 ring-1 ring-gray-300 rounded-lg w-full" v-if="paginatedToInvoiceOrdersData.length > 0 && !user.dealer">
      <table class="min-w-full divide-y divide-gray-300 border-b">
        <thead class="">
          <tr>
            <th scope="col"
              class=" px-3 py-3.5 flex-1 text-left text-sm font-semibold text-gray-900">
              Kavel
            </th>
            <th scope="col" class="px-3 py-3.5 flex-1 text-left text-sm font-semibold text-gray-900">
              Overeengekomen prijs</th>
            <th scope="col" class="px-3 py-3.5 flex-1 text-left text-sm font-semibold text-gray-900">
              Klant</th>
            <th scope="col" class="px-3 py-3.5 flex-1 text-left text-sm font-semibold text-gray-900">
              Acties</th>
          </tr>
        </thead>
        <tbody class="divide-y">
          <template v-for="lot in paginatedToInvoiceOrdersData">
            <tr>
              <td class="table-cell whitespace-nowrap px-3 py-4 text-sm text-gray-500">{{
                lot.id }}</td>
              <td class="table-cell whitespace-nowrap px-3 py-4 text-sm text-gray-500">{{
                eurFormatter.format(lot.sold_price) }}</td>
              <td class="table-cell whitespace-nowrap px-3 py-4 text-sm text-gray-500">{{
                lot.buyer_id ? lot.buyer.company_name : 'n.v.t.' }}</td>

              <td
                class="hidden lg:flex whitespace-nowrap py-4 pr-4 pl-3 text-right text-sm font-medium gap-1.5 ">
                <Link :href="'/portal/kavel/'+lot.id" class="text-royalblue-500 hover:text-royalblue-500/80">
                Bekijk kavel</Link> | <Link :href="'/portal/facturen/aanmaken?lot_id='+lot.id+'&dealer_id='+lot.buyer_id" class="text-royalblue-500 hover:text-royalblue-500/80 ">
                  Maak factuur</Link>
              </td>
              <td class="flex lg:hidden whitespace-nowrap py-4 text-sm gap-1.5 pr-3">
                <Link :href="'/portal/kavel/' + lot.id"
                class="text-royalblue-500 hover:text-royalblue-500/80">
              <font-awesome-icon :icon="['fal', 'eye']" class="h-5 w-5 shrink-0" />
              <span class="sr-only">, {{ lot.id }}</span></Link> | 
                <Link :href="'/portal/facturen/aanmaken?lot_id='+lot.id+'&dealer_id='+lot.buyer_id"
                class="text-royalblue-500 hover:text-royalblue-500/80">
              <font-awesome-icon :icon="['fal', 'file-invoice']" class="h-5 w-5 shrink-0" />
              <span class="sr-only">, {{ lot.id }}</span></Link>
            </td>
            </tr>
          </template>
        </tbody>
      </table>
    </div>
    <div v-else-if="!user.dealer">
      <div class="flex flex-col items-center justify-center h-96">
        <font-awesome-icon :icon="['fal', 'cars']" class="h-12 w-12 text-gray-400 shrink-0" />
        <p class="mt-4 text-sm text-gray-500">Geen te factureren kavels gevonden</p>
      </div>
    </div>
    
        
    <Paginator ref="toInvoiceOrdersPaginator" v-if="toInvoiceOrders.length > 10" @page="changeToInvoiceOrdersPagination" :rows="10"
      :totalRecords="toInvoiceOrders.length" :rowsPerPageOptions="[10, 20, 30]"></Paginator>
  </main>

  <!-- DELETE DIALOG -->
  <TransitionRoot as="template" :show="deleteDialog">
    <Dialog as="div" class="relative z-40" @close="deleteDialog = false">
      <TransitionChild as="template" enter="ease-out duration-300" enter-from="opacity-0" enter-to="opacity-100"
        leave="ease-in duration-200" leave-from="opacity-100" leave-to="opacity-0">
        <div class="fixed left-0 right-0 top-0 bottom-0 bg-gray-500 bg-opacity-75 transition-opacity" />
      </TransitionChild>

      <div class="fixed left-0 right-0 top-0 bottom-0 z-40 w-screen overflow-y-auto">
        <div class="flex min-h-full items-end justify-center p-4 text-center sm:items-center sm:p-0">
          <TransitionChild as="template" enter="ease-out duration-300"
            enter-from="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
            enter-to="opacity-100 translate-y-0 sm:scale-100" leave="ease-in duration-200"
            leave-from="opacity-100 translate-y-0 sm:scale-100"
            leave-to="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95">
            <DialogPanel
              class="relative transform overflow-hidden rounded-lg bg-white px-4 pb-4 pt-5 text-left shadow-xl transition-all sm:my-8 sm:w-full sm:max-w-lg sm:p-6">
              <div class="sm:flex sm:items-start">
                <div
                  class="mx-auto flex h-12 w-12 flex-shrink-0 items-center justify-center rounded-full bg-red-100 sm:mx-0 sm:h-10 sm:w-10">
                  <font-awesome-icon :icon="['fal', 'triangle-exclamation']" class="h-6 w-6 text-red-500 shrink-0" />
                </div>
                <div class="mt-3 text-center sm:ml-4 sm:mt-0 sm:text-left">
                  <DialogTitle as="h3" class="text-base font-semibold leading-6 text-gray-900">Factuur verwijderen
                  </DialogTitle>
                  <div class="mt-2">
                    <p class="text-sm text-gray-500">Je staat op het punt om factuur <span v-html="deleteDialogText"></span>
                      te verwijderen. Weet je dit zeker?</p>
                  </div>
                </div>
              </div>
              <div class="mt-5 sm:mt-4 sm:flex sm:flex-row-reverse">
                <Link preserve-scroll method="delete" :href="deleteDialogUrl" type="button"
                  class="inline-flex w-full justify-center rounded-md bg-red-600 px-3 py-2 text-sm font-semibold text-white shadow-sm hover:bg-red-500 sm:ml-3 sm:w-auto"
                  @click="deleteDialog = false" as="button">Verwijderen</Link>
                <button type="button"
                  class="mt-3 inline-flex w-full justify-center rounded-md bg-white px-3 py-2 text-sm font-semibold text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 hover:bg-gray-50 sm:mt-0 sm:w-auto"
                  @click="deleteDialog = false" ref="cancelButtonRef">Annuleren</button>
              </div>
            </DialogPanel>
          </TransitionChild>
        </div>
      </div>
    </Dialog>
  </TransitionRoot>
  <!-- END DELETE DIALOG -->

</template>